import { createApiRef, createRouteRef, createPlugin, createApiFactory, discoveryApiRef, configApiRef, createRoutableExtension, createComponentExtension, useApi, errorApiRef } from '@backstage/core-plugin-api';
import { DateTime, Duration } from 'luxon';
import React, { useEffect } from 'react';
import { Box, Paper, Typography, Grid, useTheme, makeStyles, Tooltip as Tooltip$1, Chip } from '@material-ui/core';
import { ResponsiveContainer, AreaChart, XAxis, YAxis, Tooltip, Area, ComposedChart, CartesianGrid, Legend, Line } from 'recharts';
import useDimensions from 'react-use-dimensions';
import { Progress, Link, InfoCard, Table, StatusAborted, StatusPending, StatusError } from '@backstage/core-components';
import Alert from '@material-ui/lab/Alert';
import { useAsyncFn, useAsync } from 'react-use';
import _ from 'lodash';
import { useEntity } from '@backstage/plugin-catalog-react';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const DEFAULT_PROXY_PATH = "/prometheus/api";
const SERVICE_NAME_HEADER = "x-prometheus-service-name";
const prometheusApiRef = createApiRef({
  id: "plugin.prometheus.service"
});
class PrometheusApi {
  constructor(options) {
    __publicField(this, "discoveryApi");
    __publicField(this, "configApi");
    this.discoveryApi = options.discoveryApi;
    this.configApi = options.configApi;
  }
  async getApiUrl({ serviceName }) {
    const proxyUrl = await this.discoveryApi.getBaseUrl("proxy");
    return `${proxyUrl}${this.getProxyPath({ serviceName })}`;
  }
  getUiUrl({ serviceName }) {
    if (Boolean(serviceName)) {
      const instances = this.configApi.getOptionalConfigArray(
        "prometheus.instances"
      );
      if (instances && (instances == null ? void 0 : instances.length) > 0) {
        const instance = instances.find(
          (value) => value.getString("name") === serviceName
        );
        if (Boolean(instance)) {
          return instance == null ? void 0 : instance.getOptionalString("uiUrl");
        }
      }
    }
    return this.configApi.getOptionalString("prometheus.uiUrl");
  }
  getProxyPath({ serviceName }) {
    if (Boolean(serviceName)) {
      const instances = this.configApi.getOptionalConfigArray(
        "prometheus.instances"
      );
      if (instances && (instances == null ? void 0 : instances.length) > 0) {
        const instance = instances.find(
          (value) => value.getString("name") === serviceName
        );
        if (Boolean(instance)) {
          return instance.getString("proxyPath");
        }
      }
    }
    return this.configApi.getOptionalString("prometheus.proxyPath") || DEFAULT_PROXY_PATH;
  }
  async query({
    query = `up`,
    range = { hours: 1 },
    step = 14,
    serviceName
  }) {
    const apiUrl = await this.getApiUrl({ serviceName });
    const end = DateTime.now().toSeconds();
    const start = DateTime.now().minus(Duration.fromObject(range)).toSeconds();
    const response = await fetch(
      `${apiUrl}/query_range?query=${query}&start=${start}&end=${end}&step=${step}`,
      {
        headers: {
          [SERVICE_NAME_HEADER]: serviceName || ""
        }
      }
    );
    if (!response.ok) {
      throw new Error(
        `failed to fetch data, status ${response.status}: ${response.statusText}`
      );
    }
    return response.json();
  }
  async getAlerts({ serviceName }) {
    const apiUrl = await this.getApiUrl({ serviceName });
    const response = await fetch(`${apiUrl}/rules?type=alert`, {
      headers: {
        [SERVICE_NAME_HEADER]: serviceName || ""
      }
    });
    if (!response.ok) {
      throw new Error(
        `failed to fetch data, status ${response.status}: ${response.statusText}`
      );
    }
    return response.json();
  }
}

const rootRouteRef = createRouteRef({
  id: "backstage-plugin-prometheus"
});
const backstagePluginPrometheusPlugin = createPlugin({
  id: "backstage-plugin-prometheus",
  apis: [
    createApiFactory({
      api: prometheusApiRef,
      deps: { discoveryApi: discoveryApiRef, configApi: configApiRef },
      factory: ({ discoveryApi, configApi }) => new PrometheusApi({ discoveryApi, configApi })
    })
  ],
  routes: {
    root: rootRouteRef
  }
});
const EntityPrometheusContent = backstagePluginPrometheusPlugin.provide(
  createRoutableExtension({
    name: "EntityPrometheusContent",
    component: () => import('./PrometheusContentWrapper-0d05e944.esm.js').then((m) => m.default),
    mountPoint: rootRouteRef
  })
);
const EntityPrometheusAlertCard = backstagePluginPrometheusPlugin.provide(
  createComponentExtension({
    name: "EntityPrometheusAlertCard",
    component: {
      lazy: () => import('./index-6551b309.esm.js').then(
        (m) => m.PrometheusAlertEntityWrapper
      )
    }
  })
);
const EntityPrometheusGraphCard = backstagePluginPrometheusPlugin.provide(
  createComponentExtension({
    name: "EntityPrometheusGraphCard",
    component: {
      lazy: () => import('./index-1897eaf3.esm.js').then(
        (m) => m.PrometheusGraphEntityWrapper
      )
    }
  })
);

const CustomTooltip = ({
  active,
  payload,
  label,
  coordinate,
  collector,
  metrics
}) => {
  if (payload === null)
    return null;
  if (active) {
    const { min, max } = collector.maxAndMin();
    const threshold = min.value / 30;
    const deltaY = max.y - min.y;
    const deltaValue = max.value - min.value;
    const cursorValue = min.value - deltaValue * ((min.y - coordinate.y) / deltaY);
    const points = payload.map((p) => {
      const { color, dataKey, fill, name, payload: vals } = p;
      return {
        color,
        dataKey,
        fill,
        name,
        value: vals[dataKey]
      };
    });
    const nearestPointIndexes = points.reduce(
      (acc, curr, index) => {
        const delta = Math.abs(curr.value - cursorValue);
        if (acc.length === 0)
          return delta < threshold ? [{ index, delta }] : [];
        if (Math.abs(delta - acc[0].delta) < threshold)
          return acc.concat([{ index, delta }]);
        return acc;
      },
      []
    );
    if (nearestPointIndexes.length === 0)
      return null;
    const nearestPoints = nearestPointIndexes.map(({ index }) => points[index]);
    return /* @__PURE__ */ React.createElement(Box, { margin: 3 }, /* @__PURE__ */ React.createElement(Paper, null, /* @__PURE__ */ React.createElement(Box, { padding: 2, display: "flex", flexDirection: "row" }, nearestPoints.map((nearestPoint, index) => {
      const metricForItem = metrics[nearestPoint.name];
      return /* @__PURE__ */ React.createElement(Box, { key: `nearestPoint_${index}` }, /* @__PURE__ */ React.createElement(Box, null, /* @__PURE__ */ React.createElement(Typography, { variant: "h6" }, nearestPoint.name), /* @__PURE__ */ React.createElement(Typography, { variant: "subtitle1" }, label)), Object.entries(metricForItem).map(([key, val]) => /* @__PURE__ */ React.createElement(
        Typography,
        {
          key,
          variant: "caption",
          display: "block",
          gutterBottom: true
        },
        `${key}: ${val}`
      )));
    }))));
  }
  return null;
};
function tooltipCollector() {
  const collection = [];
  let _min = { y: 0, value: 0 };
  let _max = { y: 1, value: 1 };
  function _setMaxAndMin(coll) {
    const ys = coll.map((obj) => obj.y);
    const maxY = Math.max(...ys);
    const maxYIndex = ys.indexOf(maxY);
    _max = coll[maxYIndex];
    const minY = Math.min(...ys);
    const minYIndex = ys.indexOf(minY);
    _min = coll[minYIndex];
  }
  return {
    collect: (value, y) => {
      collection.push({ value, y });
      _setMaxAndMin(collection);
    },
    maxAndMin: () => {
      return {
        max: { ..._max },
        min: { ..._min }
      };
    }
  };
}

const PROMETHEUS_RULE_ANNOTATION = "prometheus.io/rule";
const PROMETHEUS_ALERT_ANNOTATION = "prometheus.io/alert";
const PROMETHEUS_SERVICE_NAME_ANNOTATION = "prometheus.io/service-name";
const PROMETHEUS_ALERT_LABEL_ANNOTATION = "prometheus.io/labels";
const PROMETHEUS_PLUGIN_DOCUMENTATION = "https://github.com/RoadieHQ/roadie-backstage-plugins/blob/main/plugins/frontend/backstage-plugin-prometheus/README.md#entity-annotations";
const isPrometheusGraphAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[PROMETHEUS_RULE_ANNOTATION]);
};
const isPrometheusAlertAvailable = (entity) => {
  var _a;
  return Boolean((_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[PROMETHEUS_ALERT_ANNOTATION]);
};
const getServiceName = (entity) => {
  var _a;
  return (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[PROMETHEUS_SERVICE_NAME_ANNOTATION];
};
const getLabels = (entity) => {
  var _a;
  return (_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[PROMETHEUS_ALERT_LABEL_ANNOTATION];
};

function isMetricResult(resultType, _result) {
  return resultType === "matrix" || resultType === "vector";
}
function resultToGraphData(result, dimension) {
  const keys = [];
  const metrics = {};
  if (result.length === 0) {
    return { keys, metrics, data: [] };
  }
  const grouper = dimension && result.every((it) => Object.keys(it.metric).includes(dimension)) ? dimension : Object.keys(result[0].metric)[0];
  const data = _(result).flatMap((it) => {
    if (!keys.includes(it.metric[grouper])) {
      keys.push(it.metric[grouper]);
    }
    if (!Object.keys(metrics).includes(it.metric[grouper])) {
      metrics[it.metric[grouper]] = it.metric;
    }
    return it.values.map((val) => ({
      time: DateTime.fromSeconds(val[0]).toISOTime(),
      [it.metric[grouper]]: val[1]
    }));
  }).groupBy("time").map(_.spread(_.assign)).map((it) => ({ ...it, time: DateTime.fromISO(it.time).toSeconds() })).value();
  return { data, keys, metrics };
}
function useMetrics({
  query,
  range,
  step,
  dimension
}) {
  const prometheusApi = useApi(prometheusApiRef);
  const errorApi = useApi(errorApiRef);
  const { entity } = useEntity();
  const [state, fetchGraph] = useAsyncFn(async () => {
    try {
      const serviceName = getServiceName(entity);
      const uiUrl = prometheusApi.getUiUrl({ serviceName });
      const value = await prometheusApi.query({
        query,
        range,
        step,
        serviceName
      });
      if (isMetricResult(value.data.resultType, value.data.result)) {
        return { ...resultToGraphData(value.data.result, dimension), uiUrl };
      }
      errorApi.post({
        name: "Prometheus Graph construction error",
        message: "Only metric or vector result types are supported"
      });
      return Promise.reject();
    } catch (e) {
      errorApi.post({
        name: "Prometheus API Error",
        message: "Failed to retrieve graph data from prometheus API"
      });
      return Promise.reject(e);
    }
  }, []);
  return {
    loading: state.loading,
    error: state.error,
    value: state.value,
    fetchGraph
  };
}
function useAlerts(alerts) {
  const prometheusApi = useApi(prometheusApiRef);
  const { entity } = useEntity();
  const serviceName = getServiceName(entity);
  const uiUrl = prometheusApi.getUiUrl({ serviceName });
  const { value, loading, error } = useAsync(async () => {
    return await prometheusApi.getAlerts({ serviceName });
  }, []);
  if (value && value.status !== "error") {
    const rules = value.data.groups.flatMap((it) => it.rules);
    const labels = getLabels(entity);
    const parsedLabels = labels && Object.fromEntries(labels.split(",").map((label) => label.split(/=(.*)/s)));
    const displayableAlerts = rules.filter((rule) => alerts === "all" || alerts.includes(rule.name)).flatMap(
      (rule) => rule.alerts.map((alert) => ({ ...rule, ...alert, id: rule.name }))
    ).filter(
      (alert) => !parsedLabels || Object.keys(parsedLabels).every(
        (key) => alert.labels[key] === parsedLabels[key]
      )
    );
    return { loading, error, displayableAlerts, uiUrl };
  } else if (value && value.status === "error") {
    return {
      loading,
      error: { message: "Error when retrieving alerting data from Prometheus" },
      displayableAlerts: [],
      uiUrl
    };
  }
  return { loading, error, displayableAlerts: [], uiUrl };
}

const strokeColors = [
  "#8884d8",
  "#82ca9d",
  "#e74a3b",
  "#d873ea",
  "#eac473",
  "#0b4ff8",
  "#73ea79",
  "#681577",
  "#00ffb0",
  "#ff0000"
];
const useStyles$1 = (theme) => ({
  axis: {
    fill: theme.palette.text.primary
  },
  container: {
    height: 450,
    width: 1200
  },
  cartesianGrid: {
    stroke: theme.palette.textVerySubtle
  },
  chart: {
    margin: {
      right: 30,
      top: 16
    }
  },
  yAxis: {
    width: 75
  }
});
const CustomizedTick = ({ x, y, payload, collector }) => {
  const formatter = new Intl.NumberFormat("en", {
    notation: "compact"
  });
  collector.collect(payload.value, y);
  return /* @__PURE__ */ React.createElement("g", null, /* @__PURE__ */ React.createElement("text", { x, y, fill: "#5d6571", textAnchor: "end", dy: 16 }, formatter.format(payload.value)));
};
const AreaGraph = ({ data, keys, metrics }) => {
  const collector = tooltipCollector();
  const [chartRef] = useDimensions();
  const theme = useTheme();
  const styles = useStyles$1(theme);
  return /* @__PURE__ */ React.createElement(Box, { display: "flex", flexDirection: "column", ...{ ref: chartRef } }, /* @__PURE__ */ React.createElement(
    ResponsiveContainer,
    {
      width: "95%",
      height: styles.container.height,
      className: "cost-overview-chart"
    },
    /* @__PURE__ */ React.createElement(AreaChart, { data }, /* @__PURE__ */ React.createElement("defs", null, keys.map((key, idx) => /* @__PURE__ */ React.createElement(
      "linearGradient",
      {
        key,
        id: `color${key}`,
        x1: "0",
        y1: "0",
        x2: "0",
        y2: "1"
      },
      /* @__PURE__ */ React.createElement(
        "stop",
        {
          offset: "5%",
          stopColor: strokeColors[idx % 10],
          stopOpacity: 0.8
        }
      ),
      /* @__PURE__ */ React.createElement(
        "stop",
        {
          offset: "95%",
          stopColor: strokeColors[idx % 10],
          stopOpacity: 0
        }
      )
    ))), /* @__PURE__ */ React.createElement(
      XAxis,
      {
        dataKey: "time",
        domain: ["dataMin", "dataMax"],
        tickFormatter: (timeStr) => DateTime.fromSeconds(timeStr).toFormat("HH:MM:ss"),
        tickCount: 6,
        type: "number",
        stroke: styles.axis.fill
      }
    ), /* @__PURE__ */ React.createElement(YAxis, { tick: /* @__PURE__ */ React.createElement(CustomizedTick, { collector }) }), /* @__PURE__ */ React.createElement(
      Tooltip,
      {
        content: /* @__PURE__ */ React.createElement(CustomTooltip, { collector, metrics })
      }
    ), keys.map((key, idx) => /* @__PURE__ */ React.createElement(
      Area,
      {
        key,
        isAnimationActive: false,
        type: "monotone",
        dataKey: key,
        stroke: strokeColors[idx % 10],
        fillOpacity: 1,
        strokeWidth: 2,
        fill: `url(#color${key})`
      }
    )))
  ));
};
const LineGraph = ({ data, keys, metrics }) => {
  const collector = tooltipCollector();
  const [chartRef] = useDimensions();
  const theme = useTheme();
  const styles = useStyles$1(theme);
  return /* @__PURE__ */ React.createElement(Box, { display: "flex", flexDirection: "column", ...{ ref: chartRef } }, /* @__PURE__ */ React.createElement(
    ResponsiveContainer,
    {
      width: "95%",
      height: styles.container.height,
      className: "cost-overview-chart"
    },
    /* @__PURE__ */ React.createElement(ComposedChart, { margin: styles.chart.margin, data }, /* @__PURE__ */ React.createElement(CartesianGrid, { stroke: styles.cartesianGrid.stroke }), /* @__PURE__ */ React.createElement(
      XAxis,
      {
        dataKey: "time",
        domain: ["dataMin", "dataMax"],
        tickFormatter: (timeStr) => DateTime.fromSeconds(timeStr).toFormat("HH:MM:ss"),
        tickCount: 6,
        type: "number",
        stroke: styles.axis.fill
      }
    ), /* @__PURE__ */ React.createElement(YAxis, { tick: /* @__PURE__ */ React.createElement(CustomizedTick, { collector }) }), /* @__PURE__ */ React.createElement(
      Tooltip,
      {
        content: /* @__PURE__ */ React.createElement(CustomTooltip, { collector, metrics })
      }
    ), /* @__PURE__ */ React.createElement(Legend, null), keys.map((key, idx) => /* @__PURE__ */ React.createElement(
      Line,
      {
        dot: false,
        isAnimationActive: false,
        label: false,
        key,
        strokeWidth: 2,
        dataKey: key,
        stroke: strokeColors[idx % 10]
      }
    )))
  ));
};
const PrometheusGraph = ({
  query = `up`,
  range = { hours: 1 },
  step = 14,
  dimension,
  graphType = "line"
}) => {
  const { fetchGraph, value, loading, error } = useMetrics({
    query,
    range,
    step,
    dimension
  });
  useEffect(() => {
    fetchGraph();
  }, [fetchGraph]);
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, error.message);
  }
  const title = /* @__PURE__ */ React.createElement(Grid, { container: true, justifyContent: "space-between", alignItems: "center" }, /* @__PURE__ */ React.createElement(Grid, { item: true }, query), (value == null ? void 0 : value.uiUrl) && /* @__PURE__ */ React.createElement(Grid, { item: true }, /* @__PURE__ */ React.createElement(Typography, { variant: "subtitle1" }, /* @__PURE__ */ React.createElement(
    Link,
    {
      to: `${value.uiUrl}/graph?g0.expr=${encodeURI(query)}&g0.tab=0`
    },
    "Go to Prometheus"
  ))));
  const { data, keys, metrics } = value != null ? value : { data: [], keys: [], metrics: {} };
  return graphType === "line" ? /* @__PURE__ */ React.createElement(InfoCard, { title }, /* @__PURE__ */ React.createElement(LineGraph, { data, keys, metrics })) : /* @__PURE__ */ React.createElement(InfoCard, { title }, " ", /* @__PURE__ */ React.createElement(AreaGraph, { data, keys, metrics }));
};

const useStyles = makeStyles((theme) => ({
  chipRoot: {
    margin: theme.spacing(1),
    height: "100%"
  },
  chipLabel: {
    overflowWrap: "break-word",
    whiteSpace: "normal",
    textOverflow: "clip"
  }
}));
const state = (str) => {
  switch (str) {
    case "firing":
      return /* @__PURE__ */ React.createElement(StatusError, null, str);
    case "pending":
      return /* @__PURE__ */ React.createElement(StatusPending, null, str);
    case "inactive":
      return /* @__PURE__ */ React.createElement(StatusAborted, null, str);
    default:
      return /* @__PURE__ */ React.createElement(StatusAborted, null, "Unknown (", str, ")");
  }
};
const columns = [
  {
    field: "name",
    title: "Name",
    render: (row) => /* @__PURE__ */ React.createElement(Tooltip$1, { title: `${row.query}` }, /* @__PURE__ */ React.createElement("p", null, row.name))
  },
  { field: "state", title: "State", render: (row) => state(row.state) },
  {
    field: "lastEvaluation",
    title: "Last Evaluation",
    render: (row) => DateTime.fromISO(row.lastEvaluation).toLocaleString(
      DateTime.DATETIME_SHORT_WITH_SECONDS
    )
  },
  {
    field: "value",
    title: "Value",
    render: (row) => {
      const formatter = new Intl.NumberFormat("en", {
        notation: "compact"
      });
      const shortVal = formatter.format(row.value);
      return /* @__PURE__ */ React.createElement(Tooltip$1, { title: row.value }, /* @__PURE__ */ React.createElement("p", null, shortVal));
    }
  },
  {
    field: "labels",
    title: "Labels",
    render: (row) => /* @__PURE__ */ React.createElement(React.Fragment, null, Object.entries(row.labels).map(([k, v]) => /* @__PURE__ */ React.createElement(Chip, { key: k + v, label: `${k}: ${v}`, size: "small" })))
  },
  {
    field: "annotations",
    title: "Annotations",
    render: (row) => /* @__PURE__ */ React.createElement(React.Fragment, null, Object.entries(row.annotations).map(([k, v]) => /* @__PURE__ */ React.createElement(
      Chip,
      {
        key: k + v,
        label: `${k}: ${v}`,
        size: "small",
        classes: {
          root: useStyles().chipRoot,
          label: useStyles().chipLabel
        }
      }
    )))
  }
];
const PrometheusAlertStatus = ({
  alerts,
  onRowClick
}) => {
  const { error, loading, displayableAlerts, uiUrl } = useAlerts(alerts);
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  } else if (error) {
    return /* @__PURE__ */ React.createElement(Alert, { severity: "error" }, error == null ? void 0 : error.message);
  }
  const title = /* @__PURE__ */ React.createElement(Grid, { container: true, justifyContent: "space-between", alignItems: "center" }, /* @__PURE__ */ React.createElement(Grid, { item: true }, "Prometheus Alerts"), uiUrl && /* @__PURE__ */ React.createElement(Grid, { item: true }, /* @__PURE__ */ React.createElement(Typography, { variant: "subtitle1" }, /* @__PURE__ */ React.createElement(Link, { to: `${uiUrl}/alerts` }, "Go to Prometheus"))));
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(InfoCard, { title, noPadding: true }, /* @__PURE__ */ React.createElement(
    Table,
    {
      options: {
        search: false,
        paging: false,
        toolbar: false
      },
      onRowClick: onRowClick ? (_, rowData) => onRowClick(rowData) : void 0,
      data: displayableAlerts,
      columns
    }
  )));
};

const isPrometheusAvailable = (entity) => {
  var _a, _b, _c, _d;
  return Boolean((_a = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _a[PROMETHEUS_RULE_ANNOTATION]) || Boolean((_b = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _b[PROMETHEUS_ALERT_ANNOTATION]) || Boolean((_c = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _c[PROMETHEUS_SERVICE_NAME_ANNOTATION]) || Boolean((_d = entity == null ? void 0 : entity.metadata.annotations) == null ? void 0 : _d[PROMETHEUS_ALERT_LABEL_ANNOTATION]);
};

export { EntityPrometheusContent as E, PROMETHEUS_RULE_ANNOTATION as P, isPrometheusAlertAvailable as a, PROMETHEUS_ALERT_ANNOTATION as b, PROMETHEUS_PLUGIN_DOCUMENTATION as c, PrometheusAlertStatus as d, PrometheusGraph as e, backstagePluginPrometheusPlugin as f, EntityPrometheusGraphCard as g, EntityPrometheusAlertCard as h, isPrometheusGraphAvailable as i, isPrometheusAvailable as j };
//# sourceMappingURL=index-2d5393ab.esm.js.map
